import { addGlobalContexts, BrowserTracker } from '@snowplow/browser-tracker'
import { EXPERIMENTS_CONTEXT_SCHEMA } from 'constants/analytics'
import config from 'constants/config'
import { cookie, getCookies } from 'lib/cookies'
import uuidV4 from 'lib/uuidV4'

type ExperimentsData = { [k: string]: string }

function toExperimentKey(experimentId: string) {
  return `exp_${experimentId}`
}

const _currentExperimentsContextData: ExperimentsData = {
}

if (config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID) {
  _currentExperimentsContextData[toExperimentKey(config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID)] = 'pending'
}
if (config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID) {
  _currentExperimentsContextData[toExperimentKey(config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID)] = 'pending'
}

/**
 * @param dataSource Can be a new set of flags
 */
export function updateExperimentContext(key: string, value: string) {
  if (key) {
    _currentExperimentsContextData[toExperimentKey(key)] = value
  }
}

/**
 * Every time this is called, it grabs the latest value of "_currentExperimentsContextData"
 * and updates "global" context. This should append the latest experiment values to each event.
 */
function snowplowContextGenerator() {
  return {
    schema: EXPERIMENTS_CONTEXT_SCHEMA,
    data: {
      flags: Object.entries(_currentExperimentsContextData).map(([key, value]) => ({
        key,
        value: `${value}`,
      })),
    },
  }
}

let _listenerAdded = false
export function initExperimentsGlobalContextTracker() {
  if (!_listenerAdded) {
    _listenerAdded = true
    addGlobalContexts([snowplowContextGenerator])
  } else {
    console.warn('initExperimentsGlobalContextTracker is getting called more than once')
  }
}

/**
 * Taken from @snowplow/browser-tracker-core
 */

const cookieDisabledIndex = 0
const domainUserIdIndex = 1
const createTsIndex = 2
const visitCountIndex = 3
const nowTsIndex = 4
const lastVisitTsIndex = 5
const sessionIdIndex = 6
const previousSessionIdIndex = 7
const firstEventIdIndex = 8
const firstEventTsInMsIndex = 9
const eventIndexIndex = 10

const configVisitorCookieTimeout = 63072000 // 2 years
const configCookiePath = '/'
const configCookieDomain = undefined
const configCookieSameSite = 'None'
const configCookieSecure = true

/**
 * Parses the cookie values from its string representation.
 *
 * @param id Cookie value as string
 * @param domainUserId Domain user ID to be used in case of empty cookie string
 * @returns Parsed ID cookie tuple
 */
export function parseIdCookie(id: string, domainUserId?: string, memorizedSessionId?: string, memorizedVisitCount?: number) {
  const now = new Date(); const nowTs = Math.round(now.getTime() / 1000); let tmpContainer
  if (id) {
    tmpContainer = id.split('.')
    // cookies enabled
    tmpContainer.unshift('0')
  }
  else {
    tmpContainer = [
      // cookies disabled
      '1',
      // Domain user ID
      domainUserId,
      // Creation timestamp - seconds since Unix epoch
      nowTs,
      // visitCount - 0 = no previous visit
      memorizedVisitCount,
      // Current visit timestamp
      nowTs,
      // Last visit timestamp - blank meaning no previous visit
      '',
      // Session ID
      memorizedSessionId,
    ]
  }
  if (!tmpContainer[sessionIdIndex] || tmpContainer[sessionIdIndex] === 'undefined') {
    // session id
    tmpContainer[sessionIdIndex] = uuidV4()
  }
  if (!tmpContainer[previousSessionIdIndex] || tmpContainer[previousSessionIdIndex] === 'undefined') {
    // previous session id
    tmpContainer[previousSessionIdIndex] = ''
  }
  if (!tmpContainer[firstEventIdIndex] || tmpContainer[firstEventIdIndex] === 'undefined') {
    // firstEventId - blank meaning no previous event
    tmpContainer[firstEventIdIndex] = ''
  }
  if (!tmpContainer[firstEventTsInMsIndex] || tmpContainer[firstEventTsInMsIndex] === 'undefined') {
    // firstEventTs - blank meaning no previous event
    tmpContainer[firstEventTsInMsIndex] = ''
  }
  if (!tmpContainer[eventIndexIndex] || tmpContainer[eventIndexIndex] === 'undefined') {
    // eventIndex – 0 = no previous event
    tmpContainer[eventIndexIndex] = 0
  }
  const parseIntOr = function(value, defaultValue) {
    const parsed = parseInt(value)
    return isNaN(parsed) ? defaultValue : parsed
  }
  const parseIntOrUndefined = function(value) { return (value ? parseIntOr(value, undefined) : undefined) }
  const parsed = [
    tmpContainer[cookieDisabledIndex],
    tmpContainer[domainUserIdIndex],
    parseIntOr(tmpContainer[createTsIndex], nowTs),
    parseIntOr(tmpContainer[visitCountIndex], memorizedVisitCount),
    parseIntOr(tmpContainer[nowTsIndex], nowTs),
    parseIntOrUndefined(tmpContainer[lastVisitTsIndex]),
    tmpContainer[sessionIdIndex],
    tmpContainer[previousSessionIdIndex],
    tmpContainer[firstEventIdIndex],
    parseIntOrUndefined(tmpContainer[firstEventTsInMsIndex]),
    parseIntOr(tmpContainer[eventIndexIndex], 0),
  ]
  return parsed
}

export function overrideTrackerIDs(tracker: BrowserTracker, sessionId?: string, domainUserId?: string, sessionIdx?: number) {
  const idCookieName = tracker.getCookieName('id') as any as string
  const sessionIdCookieName = tracker.getCookieName('ses') as any as string
  const cookiesDict = getCookies()

  if (!cookiesDict[sessionIdCookieName]) {
    return
  }
  const parsed = parseIdCookie(cookiesDict[idCookieName])

  if (sessionId !== undefined) {
    parsed[sessionIdIndex] = sessionId
  }

  if (domainUserId === 'null') {
    console.warn('overrideTrackerIDs is getting stringified null as domainUserId')
  } else if (domainUserId !== undefined) {
    parsed[domainUserIdIndex] = domainUserId
  }

  if (sessionIdx !== undefined) {
    parsed[visitCountIndex] = sessionIdx
  }

  // Remove "0" or "1"
  parsed.shift()
  const newCookieValue = parsed.join('.')
  cookie(idCookieName, newCookieValue, configVisitorCookieTimeout, configCookiePath, configCookieDomain, configCookieSameSite, configCookieSecure)
}
